import { HttpError } from './http-error'
import type { LinkGrantChallengeResult, LinkGrantVerifyResult } from './types'

/**
 * The two link-grant operations on Auth's public surface (architecture/authorization.md §9;
 * laennec-platform-auth's committed openapi.json, LinkGrantRoutes.cs). This app calls no other
 * route on Auth, and Auth never calls this app — the whole handshake is these two requests.
 */
export interface AuthLinkGrantsClient {
  challenge(token: string): Promise<LinkGrantChallengeResult>
  verify(token: string, value: string): Promise<LinkGrantVerifyResult>
}

/** Thrown for `verify`'s and `challenge`'s uniform 410 — see docs/decisions.md D-8 for why this is
 * one type rather than trying to say which of "dead link" or "wrong answer" happened. Auth's own
 * handler deliberately erases that distinction before it reaches this client (LinkGrantRoutes.cs:
 * "the result object gives this handler no way to tell 'wrong value, grant still live' from 'the
 * grant was already dead' apart, which is deliberate ... either distinction would be a timing- and
 * status-code oracle"). */
export class LinkNoLongerUsableError extends Error {
  constructor() {
    super('This link is no longer usable.')
    this.name = 'LinkNoLongerUsableError'
  }
}

export class LinkGrantRateLimitedError extends Error {
  constructor() {
    super('Too many attempts. Try again in a moment.')
    this.name = 'LinkGrantRateLimitedError'
  }
}

export function createAuthLinkGrantsClient(baseUrl: string): AuthLinkGrantsClient {
  return {
    async challenge(token) {
      const response = await fetch(`${baseUrl}/v1/public/link-grants/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      if (response.status === 410) {
        throw new LinkNoLongerUsableError()
      }
      if (response.status === 429) {
        throw new LinkGrantRateLimitedError()
      }
      if (!response.ok) {
        throw new HttpError(response.status, `challenge failed with ${response.status}`)
      }
      return (await response.json()) as LinkGrantChallengeResult
    },

    async verify(token, value) {
      const response = await fetch(`${baseUrl}/v1/public/link-grants/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, value }),
      })
      // Every non-success outcome — wrong value with attempts left, the attempt that burns the
      // grant, or a link that was already dead — answers the identical 410 (LinkGrantRoutes.cs).
      // This client does not invent a distinction the API refuses to make; see D-8.
      if (response.status === 410) {
        throw new LinkNoLongerUsableError()
      }
      if (!response.ok) {
        throw new HttpError(response.status, `verify failed with ${response.status}`)
      }
      return (await response.json()) as LinkGrantVerifyResult
    },
  }
}
